import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Copy } from "lucide-react";
import { adminApi, errMsg, inr } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Btn, Field, PageHead, Panel, inputCls } from "@/admin/ui";

const EMPTY = { code: "", type: "fixed", value: "", max_discount: "", min_order: "", expires_on: "", max_uses: "", once_per_email: true, active: true, note: "" };

const describe = (c) => [
  c.type === "fixed" ? `${inr(c.value)} off` : `${c.value}% off${c.max_discount ? ` (max ${inr(c.max_discount)})` : ""}`,
  c.min_order ? `min. ${inr(c.min_order)}` : "",
  c.expires_on ? `until ${c.expires_on}` : "",
  c.once_per_email ? "once per customer" : "",
].filter(Boolean).join(" · ");

function CouponForm({ init, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY, ...init });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    const body = { ...f, code: f.code.trim().toUpperCase(), value: Number(f.value || 0), max_discount: Number(f.max_discount || 0), min_order: Number(f.min_order || 0), max_uses: Number(f.max_uses || 0) };
    try {
      if (init.id) await adminApi.put(`/coupons/${init.id}`, body);
      else await adminApi.post("/coupons", body);
      toast.success("Coupon saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg" data-testid="coupon-dialog">
        <DialogHeader><DialogTitle>{init.id ? "Edit coupon" : "New coupon"}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2"><Field label="Code (what customers type)"><input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="LOYAL500" className={inputCls} data-testid="coupon-code" /></Field></div>
          <Field label="Discount type">
            <div className="flex gap-2" data-testid="coupon-type">
              {[["fixed", "₹ amount"], ["percent", "% off"]].map(([v, l]) => (
                <button type="button" key={v} onClick={() => setF({ ...f, type: v })} data-testid={`coupon-type-${v}`}
                  className={`flex-1 h-10 text-xs border ${f.type === v ? "bg-black text-white border-black" : "bg-white border-zinc-300"}`}>{l}</button>
              ))}
            </div>
          </Field>
          <Field label={f.type === "fixed" ? "Amount off (₹)" : "Percentage off"}><input type="number" min="0" value={f.value} onChange={set("value")} className={inputCls} data-testid="coupon-value" /></Field>
          {f.type === "percent" && <Field label="Maximum discount ₹ (optional)"><input type="number" min="0" value={f.max_discount} onChange={set("max_discount")} className={inputCls} data-testid="coupon-max-discount" /></Field>}
          <Field label="Minimum basket ₹ (optional)"><input type="number" min="0" value={f.min_order} onChange={set("min_order")} className={inputCls} data-testid="coupon-min-order" /></Field>
          <Field label="Valid until (optional)"><input type="date" value={f.expires_on} onChange={set("expires_on")} className={inputCls} data-testid="coupon-expires" /></Field>
          <Field label="Total uses allowed (blank = unlimited)"><input type="number" min="0" value={f.max_uses || ""} onChange={set("max_uses")} className={inputCls} data-testid="coupon-max-uses" /></Field>
          <div className="col-span-2"><Field label="Note for yourself (optional)"><input value={f.note} onChange={set("note")} placeholder="e.g. for regular customers" className={inputCls} data-testid="coupon-note" /></Field></div>
          <label className="col-span-2 flex items-center gap-3 text-sm"><Switch checked={f.once_per_email} onCheckedChange={(v) => setF({ ...f, once_per_email: v })} data-testid="coupon-once" /> Each customer email can use it only once</label>
          <label className="col-span-2 flex items-center gap-3 text-sm"><Switch checked={f.active} onCheckedChange={(v) => setF({ ...f, active: v })} data-testid="coupon-active" /> Active</label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Btn variant="outline" onClick={onClose}>Cancel</Btn>
          <Btn variant="red" disabled={busy || f.code.trim().length < 3 || !Number(f.value)} onClick={save} data-testid="coupon-save">Save coupon</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Coupons() {
  const [list, setList] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => adminApi.get("/coupons").then((r) => setList(r.data));
  useEffect(() => {
    load();
  }, []);
  const del = async (c) => {
    if (!window.confirm(`Delete coupon ${c.code}?`)) return;
    await adminApi.delete(`/coupons/${c.id}`);
    toast.success("Coupon deleted");
    load();
  };
  return (
    <div className="max-w-4xl">
      <PageHead title="Coupons" desc="Create codes to share with customers. They apply to the whole basket (records, turntables and accessories) at checkout.">
        <Btn onClick={() => setEdit({})} data-testid="coupons-add"><Plus className="w-4 h-4" /> New coupon</Btn>
      </PageHead>
      <Panel className="p-0 sm:p-0">
        {list?.length === 0 && <p className="p-8 text-sm text-zinc-500" data-testid="coupons-empty">No coupons yet.</p>}
        <ul className="divide-y divide-zinc-100" data-testid="coupons-list">
          {list?.map((c) => (
            <li key={c.id} className="flex flex-wrap items-center gap-4 px-5 py-4" data-testid={`coupon-row-${c.code}`}>
              <div className="flex-1 min-w-[220px]">
                <p className="font-bold font-arial flex items-center gap-2">{c.code}
                  <button onClick={() => navigator.clipboard?.writeText(c.code).then(() => toast.success("Code copied"))} className="text-zinc-400 hover:text-red" aria-label="Copy code" data-testid={`coupon-copy-${c.code}`}><Copy className="w-3.5 h-3.5" /></button>
                  {!c.active && <span className="text-[10px] uppercase bg-zinc-200 px-1.5 py-0.5">Off</span>}
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">{describe(c)}{c.note && ` · ${c.note}`}</p>
              </div>
              <span className="text-xs text-zinc-600 font-arial" data-testid={`coupon-uses-${c.code}`}>Used {c.uses || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</span>
              <Btn variant="outline" className="h-9 px-3" onClick={() => setEdit(c)} data-testid={`coupon-edit-${c.code}`}>Edit</Btn>
              <button onClick={() => del(c)} className="text-zinc-400 hover:text-red" aria-label="Delete coupon" data-testid={`coupon-delete-${c.code}`}><Trash2 className="w-4 h-4" /></button>
            </li>
          ))}
        </ul>
      </Panel>
      {edit && <CouponForm init={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
